/**
 * Gets the IE/Edge version.
 *
 * @returns `false` or the IE/Edge version as a number
 */
function getIEVersion() {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const ua = navigator.userAgent;
    const msie = ua.indexOf('MSIE ');
    if (msie > 0) {
        // IE 10 or older => return version number
        return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
    }
    const trident = ua.indexOf('Trident/');
    if (trident > 0) {
        // IE 11 => return version number
        const rv = ua.indexOf('rv:');
        return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
    }
    const edge = ua.indexOf('Edge/');
    if (edge > 0) {
        // Edge (IE 12+) => return version number
        return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
    }
    // other browser
    return false;
}

/**
 * Whether the current browser is IE/Edge
 */
const isIE = getIEVersion() !== false;

/**
 * Whether the current browser is Chrome
 */
const isChrome = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return navigator.userAgent.match(/Chrome((?!Edge).)*$/g) !== null;
};

/**
 * Whether the current browser is Edge
 */
const isEdge = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return navigator.userAgent.match(/Edge/g) !== null;
};

/**
 * Whether the current browser is Firefox
 */
const isFirefox = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
};

/**
 * Whether the current browser is Safari and thus needs extra special treatment.
 */
const isSafari = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return !!navigator.userAgent.match(/Version\/[\d.]+.*Safari/);
};

/**
 * Whether the current device is running Android
 */
const isAndroid = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return navigator.userAgent.toLowerCase().indexOf('android') > -1;
};
var isAndroid$1 = isAndroid();

/**
 * Whether the current device is running iOS
 */
const regex = /iPhone|iPad/i;
const isIOS = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return (navigator.platform === 'iPhone' ||
        navigator.platform === 'iPod' ||
        navigator.platform === 'iPad' ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1 && !window.MSStream) ||
        regex.test(navigator.userAgent));
};

/**
 * Whether the current device is an iPad Pro.
 */
const _isIpadPro = () => {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) || false;
};
const isIpadPro = _isIpadPro();

function _isNotchIPhone() {
    if (typeof navigator === 'undefined') {
        return false;
    }
    let iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    // Get the device pixel ratio
    let ratio = window.devicePixelRatio || 1;
    // Define the users device screen dimensions
    let screen = {
        width: window.screen.width * ratio,
        height: window.screen.height * ratio,
    };
    // iPhone X+ detection
    if (iOS && screen.width >= 1125 && screen.height >= 2436) {
        return true;
    }
    return false;
}
/**
 * Whether the current device is an iPhone with a display notch.
 */
const isNotchIPhone = _isNotchIPhone();

function _isMobile() {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const a = navigator.userAgent || navigator.vendor || window.opera;
    // eslint-disable-next-line max-len
    const regex1 = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
    // eslint-disable-next-line max-len
    const regex2 = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd\co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i;
    return regex1.test(a) || regex2.test(a.substr(0, 4));
}
/**
 * Whether the current device is a phone.
 */
const isMobile = _isMobile();

function _isHandheld() {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return (isIpadPro ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Opera Mobile|Kindle|Windows Phone|PSP|AvantGo|Atomic Web Browser|Blazer|Chrome Mobile|Dolphin|Dolfin|Doris|GO Browser|Jasmine|MicroB|SamsungBrowser|Mobile Firefox|Mobile Safari|Mobile Silk|Motorola Internet Browser|NetFront|NineSky|Nokia Web Browser|Obigo|Openwave Mobile Browser|Palm Pre web browser|Polaris|PS Vita browser|Puffin|QQbrowser|SEMC Browser|Skyfire|Tear|TeaShark|UC Browser|uZard Web|wOSBrowser|Yandex.Browser mobile/i.test(ua));
}
/**
 * Whether the current device is a handheld one.
 *
 * This includes phones and tablets.
 */
const isHandheld = _isHandheld();

function _isTablet() {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const ua = (navigator.userAgent || navigator.vendor || window.opera).toLowerCase();
    // eslint-disable-next-line max-len
    const regex1 = /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(ua);
    // eslint-disable-next-line max-len
    return regex1 || isIpadPro;
}
/**
 * Whether the current device is a tablet.
 */
const isTablet = _isTablet();

function _hasTouch() {
    if (typeof navigator === 'undefined') {
        return false;
    }
    return ('ontouchstart' in window ||
        'ontouchstart' in document.documentElement ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0 ||
        navigator.MaxTouchPoints > 0);
}
/**
 * Whether the current device has a touch input.
 */
const hasTouch = _hasTouch();

/**
 * Gets the Chrome version.
 *
 * @returns `false` or the Chrome version number
 */
function getChromeVersion() {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const raw = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
    return raw && !isIE ? parseInt(raw[2], 10) : false;
}

/**
 * Gets the Firefox version.
 *
 * @returns `false` or the Firefox version
 */
function getFirefoxVersion() {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const match = navigator.userAgent.match(/Firefox\/([0-9]+)\./);
    return match ? parseInt(match[1], 10) : false;
}

/**
 * Gets the iOS version.
 *
 * @returns `false` or the iOS version as a string
 */
function getIOSVersion() {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const test = navigator.userAgent.match(/Version\/([0-9.]+)\s(Safari|Mobile)\//);
    if (test && test.length > 1)
        return test[1];
    return false;
}

/**
 * Returns the Safari version.
 *
 * @returns `false` or the Safari version as a string
 */
function getSafariVersion() {
    if (typeof navigator === 'undefined') {
        return false;
    }
    const match = navigator.userAgent.match(/Version\/[\d.]+.*Safari/);
    return match ? match[0].substring(match[0].indexOf('/') + 1, match[0].indexOf(' ')) : false;
}

export { getChromeVersion, getFirefoxVersion, getIEVersion, getIOSVersion, getSafariVersion, hasTouch, isAndroid$1 as isAndroid, isChrome, isEdge, isFirefox, isHandheld, isIE, isIOS, isIpadPro, isMobile, isNotchIPhone, isSafari, isTablet };
